<template>
  <section-card
    :title="t('home_cockpit.news.title')"
    :subtitle="t('home_cockpit.news.subtitle')"
    view-all-to="/news"
    :is-loading="isLoading && items.length === 0"
    :is-empty="!isLoading && items.length === 0"
    :empty-title="t('home_cockpit.news.empty_title')"
    compact
  >
    <!-- Category filter chips -->
    <div class="flex flex-wrap gap-1 mb-2">
      <button
        v-for="cat in categoryChips"
        :key="cat.value || 'all'"
        type="button"
        :class="[
          'inline-flex items-center text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full',
          activeCategory === cat.value
            ? 'bg-theme-palette-emerald text-theme-text-inverse dark:bg-theme-palette-emerald'
            : 'bg-theme-bg-subtle text-theme-text-secondary hover:bg-theme-surface-raised dark:bg-theme-surface-raised dark:hover:bg-theme-surface-elevated',
        ]"
        @click="onCategory(cat.value)"
      >
        {{ cat.label }}
      </button>
    </div>

    <!-- Skeleton: 8 ghost news rows (the API also fetches size=8) so the
         sticky right column claims its full height before the page paints. -->
    <template #loadingSkeleton>
      <div class="divide-y divide-theme-border dark:divide-theme-border" aria-hidden="true">
        <div v-for="i in 8" :key="`news-skel-${i}`" class="py-2 space-y-1">
          <div class="flex items-center gap-2">
            <skeleton-text width="42px" height="10px" />
            <skeleton-text width="32px" height="9px" />
          </div>
          <skeleton-text :width="i % 3 === 0 ? '70%' : i % 2 === 0 ? '85%' : '95%'" height="13px" />
          <skeleton-text v-if="i % 2 === 0" width="50%" height="11px" />
        </div>
      </div>
    </template>

    <div class="divide-y divide-theme-border dark:divide-theme-border">
      <news-item
        v-for="item in items"
        :key="item.id"
        :title="item.title"
        :category="item.category"
        :source="item.source"
        :href="item.url"
        :timestamp="item.timestamp"
      />
    </div>
  </section-card>
</template>

<script setup>
  /* eslint-disable no-undef */
  import NewsItem from '@/components/ui/data-display/NewsItem.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import NewsroomReaderRepository from '@/features/newsroom-reader/api/NewsroomReaderRepository';
  import { openDemoPaywall } from '@/composables/useDemoDraft';
  import StatisticsRepository from '@/repositories/StatisticsRepository';
  import { normalizeItem } from '@/pages/news/domain/news';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({ demo: Boolean });
  const { t } = useI18n();

  const items = ref([]);
  const isLoading = ref(false);
  const activeCategory = ref(null);

  // API values are lowercase enum-ish strings (energieprijzen, olie,
  // beleid_regelgeving, koolstofmarkten, netinfrastructuur, ...). The chip
  // labels stay friendly; the `value` is what gets sent to /news?category=.
  const categoryChips = computed(() => [
    { value: null, label: t('home_cockpit.news.filter_all') },
    { value: 'elektriciteit', label: t('home_cockpit.news.filter_electricity') },
    { value: 'gas', label: t('home_cockpit.news.filter_gas') },
    { value: 'olie', label: t('home_cockpit.news.filter_oil') },
    { value: 'koolstofmarkten', label: t('home_cockpit.news.filter_co2') },
    { value: 'beleid_regelgeving', label: t('home_cockpit.news.filter_policy') },
    { value: 'netinfrastructuur', label: t('home_cockpit.news.filter_grid') },
    { value: 'energieprijzen', label: t('home_cockpit.news.filter_prices') },
  ]);

  async function load(category) {
    isLoading.value = true;
    try {
      const response = props.demo ? await NewsroomReaderRepository.externalFeed({ limit: 8 }) : await StatisticsRepository.getNews({
        size: 8,
        energy_relevant: true,
        category: category || undefined,
      });
      const source = props.demo ? (response?.data?.articles || []) : (Array.isArray(response?.data) ? response.data : []);
      items.value = source
        .map((it) => {
          const n = normalizeItem(it);
          return {
            id: n.id || it.id || it.url,
            title: n.title || it.title,
            category: n.category || it.category,
            source: n.source || it.source,
            url: n.url || it.url,
            timestamp: n.timestamp || it.timestamp || n.timestampMs,
          };
        })
        .filter((item) => item.title);
    } catch (e) {
      items.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  function onCategory(value) {
    if (props.demo) { openDemoPaywall(); return; }
    activeCategory.value = value;
    load(value);
  }

  onMounted(() => load(null));

  defineExpose({ refresh: () => load(activeCategory.value) });
</script>
